import classNames from 'classnames'
import CloseIcon from 'mdi-react/CloseIcon'
import ContentSaveIcon from 'mdi-react/ContentSaveIcon'
import EditIcon from 'mdi-react/EditIcon'
import React from 'react'

import { createStyles, IconButton, TextField, Theme, Typography } from '@mui/material'
import { withStyles, WithStyles } from '@mui/styles'

import Cell, { CellProps } from '../Cell'

class RowCell extends React.Component<RowCellProps & WithStyles<typeof styles>, RowCellState> {
  readonly state = {
    editing: false,
    value: undefined,
  }

  resetInitialValue = {}

  toggleEditing = () => {
    this.setState((state) => ({
      editing: !state.editing,
      value: this.props.initialContent,
    }))
  }

  onTextFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({
      value: event.target.value,
    })
  }

  catchSpecialKeys = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case 'Enter':
        this.onClickSave()
        event.preventDefault()
        break
      case 'Escape':
        this.toggleEditing()
        event.preventDefault()
        break
      default:
    }
  }

  onClickSave = () => {
    const { onSave } = this.props
    const { value } = this.state

    if (onSave) {
      onSave(value)
    }
    this.toggleEditing()
  }

  render() {
    const {
      classes,
      rowType = RowType.default,
      contentByRowType,
      tooltipByRowType,
      editable,
      initialContent,
      ...cellProps
    } = this.props
    const { editing } = this.state
    const content = contentByRowType[rowType]
    const cellTooltip = !!tooltipByRowType ? tooltipByRowType[rowType] : ''

    let cellContent = content
    const cellClassNames: string[] = []

    if (rowType !== RowType.header) {
      if (editable && editing) {
        const defaultValue = typeof initialContent === 'string' ? initialContent : undefined
        cellContent = (
          <>
            <TextField
              margin="dense"
              defaultValue={defaultValue}
              inputProps={{ className: classes.input }}
              onKeyUp={this.catchSpecialKeys}
              onChange={this.onTextFieldChange}
              autoFocus
            />
            <IconButton aria-label="Save" onClick={this.onClickSave} className={classes.iconButton}>
              <ContentSaveIcon className={classes.icon} />
            </IconButton>
            <IconButton aria-label="Cancel" onClick={this.toggleEditing} className={classes.iconButton}>
              <CloseIcon className={classes.icon} />
            </IconButton>
          </>
        )
      } else if (typeof content === 'string') {
        cellClassNames.push(classes.cell)
        cellContent = (
          <>
            <Typography
              variant="body2"
              className={rowType === RowType.placeHolder ? classes.placeHolderText : undefined}
              noWrap
            >
              {content}
            </Typography>
            {editable ? (
              <span className={classNames(EDIT_BUTTON_CONTAINER_CLASS, classes.editIconButtonContainer)}>
                <IconButton aria-label="Edit" onClick={this.toggleEditing} className={classes.editIconButton}>
                  <EditIcon className={classes.icon} />
                </IconButton>
              </span>
            ) : null}
          </>
        )
      }
    }

    return (
      <Cell {...cellProps} tooltip={cellTooltip} className={classNames(cellProps.className, cellClassNames)}>
        {cellContent}
      </Cell>
    )
  }
}

export enum RowType {
  header = 'header',
  placeHolder = 'placeHolder',
  default = 'default',
}

const EDIT_BUTTON_CONTAINER_CLASS = 'edit-button'

type RowCellState = {
  editing: boolean
  value: string | number | undefined
}

export type RowCellProps = {
  rowType?: RowType
  contentByRowType: { [key: string]: React.ReactNode }
  tooltipByRowType?: { [key: string]: string }
  editable?: boolean
  onSave?: (value?: string) => void
  initialContent?: string | number
} & Partial<CellProps>

export const styles = ({ palette, spacing, typography, transitions }: Theme) =>
  createStyles({
    cell: {
      [`&:not(:hover) .${EDIT_BUTTON_CONTAINER_CLASS}`]: {
        opacity: 0,
      },
    },
    placeHolderText: {
      color: palette.text.secondary,
    },
    icon: {
      width: spacing(2),
      height: spacing(2),
    },
    iconButton: {
      padding: spacing(),
    },
    editIconButtonContainer: {
      position: 'absolute',
      right: 0,
      boxShadow: `0 0 ${spacing()} ${spacing(0.5)}px ${palette.grey['200']}`,
      backgroundColor: palette.grey['200'],
      borderRadius: '100%',
      opacity: 1,
      transition: transitions.create('all'),
    },
    editIconButton: {
      padding: spacing(),
    },
    input: {
      fontSize: typography.body1.fontSize,
      paddingBottom: spacing(0.75),
    },
  })

export default withStyles(styles)(RowCell)
